import {
	Component,
	Injector,
} from '@angular/core';

import {
	UntypedFormControl,
	NgForm,
} from '@angular/forms';

import * as Inputmask from 'inputmask';
import { finalize } from 'rxjs/operators';
import { UIRouter } from '@uirouter/angular';

import {
	findLast,
	includes,
} from 'lodash';

import { CommonBaseComponent } from '../../../Common/components/base.component';
import { CommonSecondsFormatterPipe } from '../../../Common/pipes/seconds_formatter.pipe';
import { CommonAppDataService } from '../../../Common/services/app_data/app_data.service';
import { CommonUserDataService } from '../../../Common/services/user_data.service';
import { CommonServerDateService } from '../../../Common/services/server_date.service';

import {
	AuthenticationMeetingsUserService,
	IAuthenticationMeetingsUserActionsResponse,
} from '../authentication/services/authentication.meetings.user.service';
import { AuthenticationMeetingsErrorService } from '../authentication/services/authentication.meetings.error.service';
import { MeetingsAuthAnimation } from '../animation/animation';
import { IAuthenticationMeetingsConfirmUser } from '../authentication/authentication.interfaces';

@Component({
	selector: 'authentication-meetings-confirm',
	templateUrl: './authentication.meetings.confirm.component.pug',
	animations: [
		MeetingsAuthAnimation,
	],
})
export class AuthenticationMeetingsConfirmComponent extends CommonBaseComponent {
	state = {
		isLoading: false,
		resendDelaySeconds: 0,
		isSending: false,
	};

	data: IAuthenticationMeetingsConfirmUser = {
		Code: '',
	};

	resource = {
		validationCustomErrors: {
			custom: '',
		},
		formattedPhoneNumber: '',
	};


	onChangeFieldHandler = this.authenticationMeetingsErrorService.onChangeFieldHandler;

	private stopResendTimer: number;

	constructor (
		protected injector: Injector,
		private authenticationMeetingsUserService: AuthenticationMeetingsUserService,
		private router: UIRouter,
		private authenticationMeetingsErrorService: AuthenticationMeetingsErrorService,
		private commonUserDataService: CommonUserDataService,
		private commonServerDateService: CommonServerDateService,
		private commonSecondsFormatterPipe: CommonSecondsFormatterPipe,
		private commonAppDataService: CommonAppDataService,
	) {
		super(injector);
	}

	ngOnInit (): void {
		if (this.commonUserDataService.getDataByPath('nextPhoneConfirmationAttemptTime')) {
			this.resource.formattedPhoneNumber = Inputmask.format(
				this.commonUserDataService.getDataByPath('phoneNumber'), {
					alias: 'casem-phone-numb-11',
				},
			);

			this.initResendTimer();
			this.autofillConfirmationCode();
		} else {
			this.router.stateService.go('login');
		}
	}

	ngOnDestroy (): void {
		clearInterval(this.stopResendTimer);
	}

	confirm (form: NgForm): void {
		if (form.valid) {
			this.state.isLoading = true;

			this.authenticationMeetingsUserService
				.confirm(this.data)
				.pipe(
					finalize(() => this.state.isLoading = false),
				)
				.subscribe((response: IAuthenticationMeetingsUserActionsResponse) => {
					if (response.IsSuccess) {
						this.router.dispose();
						const authenticationReturnUrl = this.commonAppDataService.getDataByPath('authenticationReturnUrl') || '/';

						window.location.assign(authenticationReturnUrl);

						if (this.isAuthenticationReturnFrontUrl(authenticationReturnUrl)) {
							// перезагрузка страницы если это бековская ссылка приведет к потере редиректа.
							// если же ссылка фронтовая с содержанием хэша то нам нужна перезагрузка страницы чтобы загрузилось нужное ангуляр приложение. и включился роутер.
							window.location.reload();
						}
					} else {
						this.authenticationMeetingsErrorService.handleSuccessResponse(response, this.resource.validationCustomErrors, form.controls.code as UntypedFormControl);
					}
				});
		}
	}

	initResendTimer (): void {
		this.state.resendDelaySeconds = this.getResendDelaySeconds();

		if (this.state.resendDelaySeconds) {
			this.stopResendTimer = window.setInterval(() => {
				this.state.resendDelaySeconds--;

				if (!this.state.resendDelaySeconds) {
					clearInterval(this.stopResendTimer);
				}
			}, 1000);
		}
	}

	getResendDelaySeconds (): number {
		const nextPhoneConfirmationAttemptTime = this.commonUserDataService.getDataByPath('nextPhoneConfirmationAttemptTime');
		const now = this.commonServerDateService.getMoscowDateTime();
		const confirmationAttempt = this.commonServerDateService.getMoscowDateTime(nextPhoneConfirmationAttemptTime);
		const diff = now.diff(confirmationAttempt, 'seconds', true);

		return !nextPhoneConfirmationAttemptTime ? 0 : Math.round(Math.abs(diff || 0));
	}

	getWaitingTimerText (): string {
		return this.instant('meetings_auth.forms.confirm.controls.timer_text.title', {
			time: this.commonSecondsFormatterPipe.transform(this.state.resendDelaySeconds),
		});
	}

	autofillConfirmationCode (): void {
		const codeLength = 4;
		const phone = this.commonUserDataService.getDataByPath('phoneNumber');
		const cookieWithCode = findLast(document.cookie.split(';'), (cookie) => includes(cookie, `SendTo-${phone}`));

		if (cookieWithCode) {
			const code = decodeURIComponent(cookieWithCode).slice(-codeLength);

			if (code) {
				this.data.Code = code;
			}
		}
	}

	resendCode (form: NgForm): void {
		if (!this.state.isSending) {
			this.state.isSending = true;

			this.authenticationMeetingsUserService.sendCode()
				.pipe(
					finalize(() => this.state.isSending = false),
				)
				.subscribe((response: IAuthenticationMeetingsUserActionsResponse) => {
					if (response.IsSuccess) {
						this.commonUserDataService.setDataByPath(response.Result.ExpiresAt, 'nextPhoneConfirmationAttemptTime');
						this.initResendTimer();
						this.autofillConfirmationCode();
					} else {
						this.authenticationMeetingsErrorService.handleSuccessResponse(response, this.resource.validationCustomErrors, form.controls.code as UntypedFormControl);
					}
				});
		}
	}

	private isAuthenticationReturnFrontUrl (authenticationReturnUrl: string): boolean {
		const splittedFullUrl = (location.origin + authenticationReturnUrl).split('#');
		const isMatchedUrls = splittedFullUrl[0] === this.commonAppDataService.getDataByPath('appFullLocationUrl');
		const hasHash = !!splittedFullUrl[1];
		return isMatchedUrls && hasHash;
	}
}
